import { parseISO } from "date-fns";
// helpers
import { handleErrorAlert } from "../../../common/utils/helpers/handleErrorAlert";
import { getFormattedDate } from "../../../common/utils/helpers/getFormattedDate";
import ApiHelper from "../../../common/utils/apiHelper";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import {
  buildAirlineMilesAndMoreBookingFields,
  readAirlineMilesAndMoreNumber
} from "../../../common/utils/transport/airlineBookingPayload";
import { type TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
import { resolveMunichCityPassApiPaths } from "./helpers/resolveMunichCityPassApiPaths";
import {
  getLeadGuest,
  getPassengerCategoryByBirthDate,
  getPassengerFullName,
  prefillGuestName
} from "../shared/helpers/leadGuest";
// actions / selectors
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import {
  type MunichCityPassBookingForm,
  type MunichCityPassOffer,
  type MunichCityPassOffersResponse,
  type MunichCityPassPassenger
} from "./types";

// Toggle mock for Munich City Pass offers + estimation
const USE_MOCK_MUNICH_CITY_PASS = false;
// Toggle to make the mocked estimation fail with a 400 validation error (needs USE_MOCK_MUNICH_CITY_PASS = true)
const USE_MOCK_MUNICH_CITY_PASS_ESTIMATION_ERROR = false;

export const initialMunichCityPassPassenger: MunichCityPassPassenger = {
  firstName: "",
  lastName: "",
  birthDate: new Date("2001-01-01"),
  hasRemove: false
};

const MOCK_MUNICH_CITY_PASS_OFFERS_RESPONSE: MunichCityPassOffersResponse = {
  provider_title: "Munich City Pass - All-Inclusive",
  provider_description:
    "The Munich City Pass is the official guest card of the City of Munich. Enjoy free public transport and included attractions.",
  provider_logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/390c0357-516c-45ae-91b6-152fb9996817/65249b87c14fafd.png",
    name: "munich-city-pass"
  },
  highlight:
    "<p>Valid on Munich’s entire public transport network, including the Airport zone.</p><p>Choose your pass duration, then add passengers below.</p>",
  cancellation_policy: "Cancellation Policy",
  items: [
    {
      name: "1 Day",
      offer_code: "MUNICH_CITY_PASS_1_DAY",
      cancellation_policy: "Cancellation Policy",
      has_family_card: false
    },
    {
      name: "2 Days",
      offer_code: "MUNICH_CITY_PASS_2_DAYS",
      cancellation_policy: "Cancellation Policy",
      has_family_card: false
    },
    {
      name: "3 Days",
      offer_code: "MUNICH_CITY_PASS_3_DAYS",
      cancellation_policy: "Cancellation Policy",
      has_family_card: false
    },
    {
      name: "4 Days",
      offer_code: "MUNICH_CITY_PASS_4_DAYS",
      cancellation_policy: "Cancellation Policy",
      has_family_card: false
    },
    {
      name: "5 Days",
      offer_code: "MUNICH_CITY_PASS_5_DAYS",
      cancellation_policy: "Cancellation Policy",
      has_family_card: false
    }
  ]
};

const MOCK_MUNICH_CITY_PASS_ESTIMATION_RESPONSE = {
  formatted_price: "79.00 EUR",
  miles_and_more: "250",
  details: {
    session_id: "mock-munich-city-pass-session",
    passengers: [
      {
        first_name: "Ada",
        last_name: "Adult",
        birthdate: "2000-01-01T00:00:00+01:00",
        country_of_residence: null,
        member_card: false,
        passenger_type: "Adult",
        price: "39.50",
        currency: "EUR"
      },
      {
        first_name: "Yara",
        last_name: "Youth",
        birthdate: "2010-01-01T00:00:00+01:00",
        country_of_residence: null,
        member_card: false,
        passenger_type: "Adult",
        price: "39.50",
        currency: "EUR"
      }
    ]
  }
};

const MOCK_MUNICH_CITY_PASS_ESTIMATION_ERROR_RESPONSE = {
  code: 400,
  message: "Validation failed",
  debug: null,
  fields: [
    {
      field: "details.passengers[1].birthdate",
      errorMessage: "Children under 6 years old travel free of charge and do not require a ticket."
    }
  ]
};

export const munichCityPassActionTypes = {
  RESET_MUNICH_CITY_PASS_STATE: "RESET_MUNICH_CITY_PASS_STATE",
  LOAD_MUNICH_CITY_PASS_OFFERS: "LOAD_MUNICH_CITY_PASS_OFFERS",
  SET_MUNICH_CITY_PASS_SELECTED_OFFER: "SET_MUNICH_CITY_PASS_SELECTED_OFFER",
  UPDATE_MUNICH_CITY_PASS_PASSENGERS_LIST: "UPDATE_MUNICH_CITY_PASS_PASSENGERS_LIST",
  SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO: "SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO",
  UPDATE_MUNICH_CITY_PASS_SHOW_VALIDATION_ERRORS: "UPDATE_MUNICH_CITY_PASS_SHOW_VALIDATION_ERRORS",
  UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR: "UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR",
  SET_MUNICH_CITY_PASS_BOOKING_FORM: "SET_MUNICH_CITY_PASS_BOOKING_FORM",
  TOGGLE_MUNICH_CITY_PASS_BOOKING_ERROR: "TOGGLE_MUNICH_CITY_PASS_BOOKING_ERROR",
  SET_MUNICH_CITY_PASS_BOOKING_RESPONSE_DATA: "SET_MUNICH_CITY_PASS_BOOKING_RESPONSE_DATA",
  TOGGLE_MUNICH_CITY_PASS_PAYMENT_MODAL: "TOGGLE_MUNICH_CITY_PASS_PAYMENT_MODAL"
};

const EMPTY_BOOKING_FORM: MunichCityPassBookingForm = {
  nameForm: "",
  emailForm: "",
  commentForm: ""
};

export const initialState = {
  offers: null as MunichCityPassOffersResponse,
  selectedOffer: null as MunichCityPassOffer | null,
  passengerList: [{ ...initialMunichCityPassPassenger }] as MunichCityPassPassenger[],
  prebookingInfoResponse: null as any,
  prebookingError: null as any,
  showValidationErrors: false,
  dataForBooking: { ...EMPTY_BOOKING_FORM } as MunichCityPassBookingForm,
  // Guest name last prefilled from the lead guest; tells a prefilled name apart from one the guest typed.
  prefilledNameForm: "",
  bookingError: false,
  bookingResponseData: null as any,
  showPaymentModal: false
};

const munichCityPassModule = (state = initialState, action: any) => {
  switch (action.type) {
    case munichCityPassActionTypes.RESET_MUNICH_CITY_PASS_STATE:
      return {
        ...initialState,
        passengerList: [{ ...initialMunichCityPassPassenger }],
        dataForBooking: { ...EMPTY_BOOKING_FORM }
      };
    case munichCityPassActionTypes.LOAD_MUNICH_CITY_PASS_OFFERS:
      return {
        ...state,
        offers: action.offers
      };
    case munichCityPassActionTypes.SET_MUNICH_CITY_PASS_SELECTED_OFFER:
      return {
        ...state,
        selectedOffer: action.selectedOffer,
        prebookingInfoResponse: null,
        prebookingError: null
      };
    case munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PASSENGERS_LIST:
      return {
        ...state,
        passengerList: action.passengerList
      };
    case munichCityPassActionTypes.SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO: {
      const nextState = {
        ...state,
        prebookingInfoResponse: action.prebookingInfoResponse
      };

      if (!action.prebookingInfoResponse) {
        return nextState;
      }

      // Prefill the guest name with the lead guest when the summary opens, unless the guest typed their own.
      // Age is checked on the travel date (`yyyy-MM-dd`); without it, on today.
      const travelDate = action.travelDate ? parseISO(action.travelDate) : null;
      const leadGuest = getLeadGuest(state.passengerList, passenger =>
        getPassengerCategoryByBirthDate(passenger.birthDate, travelDate)
      );

      return prefillGuestName(nextState, getPassengerFullName(leadGuest?.firstName, leadGuest?.lastName));
    }
    case munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_SHOW_VALIDATION_ERRORS:
      return {
        ...state,
        showValidationErrors: action.showValidationErrors
      };
    case munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR:
      return {
        ...state,
        prebookingError: action.prebookingError
      };
    case munichCityPassActionTypes.SET_MUNICH_CITY_PASS_BOOKING_FORM:
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    case munichCityPassActionTypes.TOGGLE_MUNICH_CITY_PASS_BOOKING_ERROR:
      return {
        ...state,
        bookingError: action.bookingError
      };
    case munichCityPassActionTypes.SET_MUNICH_CITY_PASS_BOOKING_RESPONSE_DATA:
      return {
        ...state,
        bookingResponseData: action.bookingResponseData
      };
    case munichCityPassActionTypes.TOGGLE_MUNICH_CITY_PASS_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: action.showPaymentModal
      };
    default:
      return state;
  }
};

export default munichCityPassModule;

export const munichCityPassActionCreators = {
  resetMunichCityPassStateAction: () => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.RESET_MUNICH_CITY_PASS_STATE
    });
  },
  setSelectedOfferAction: (selectedOffer: MunichCityPassOffer | null) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_SELECTED_OFFER,
      selectedOffer
    });
  },
  updatePassengersAction: (passengerList: MunichCityPassPassenger[]) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PASSENGERS_LIST,
      passengerList
    });
  },
  updateShowValidationErrorsAction: (showValidationErrors: boolean) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_SHOW_VALIDATION_ERRORS,
      showValidationErrors
    });
  },
  updateBookingFormAction: (dataForBooking: MunichCityPassBookingForm) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_BOOKING_FORM,
      dataForBooking
    });
  },
  clearPrebookingErrorAction: () => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR,
      prebookingError: null
    });
  },
  resetMunichCityPassPrebookStateAction: () => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO,
      prebookingInfoResponse: null
    });
  },
  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.TOGGLE_MUNICH_CITY_PASS_BOOKING_ERROR,
      bookingError
    });
  },
  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({
      type: munichCityPassActionTypes.TOGGLE_MUNICH_CITY_PASS_PAYMENT_MODAL,
      showPaymentModal
    });
  },
  getMunichCityPassOffersAction:
    (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const paths = resolveMunichCityPassApiPaths(transportContext);

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      if (paths.kind === "invalid_airline") {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
        return;
      }

      try {
        let data: MunichCityPassOffersResponse;

        if (USE_MOCK_MUNICH_CITY_PASS) {
          // keep a small delay so loading UX matches real API
          await new Promise(resolve => setTimeout(resolve, 300));
          data = MOCK_MUNICH_CITY_PASS_OFFERS_RESPONSE;
        } else {
          const res = await ApiHelper.get(`${apiUrl}/${paths.mainPath}`);
          // Catalog load is synchronous HTTP (guest + airline). WebSocket follows POST estimation only.
          data = (res?.data ?? null) as MunichCityPassOffersResponse;
        }

        dispatch({
          type: munichCityPassActionTypes.LOAD_MUNICH_CITY_PASS_OFFERS,
          offers: data
        });

        // Preselect first offer only when nothing is selected yet
        const currentSelectedOffer = getState().munichCityPass.selectedOffer as MunichCityPassOffer | null;
        if (!currentSelectedOffer && data?.items?.[0]) {
          dispatch(munichCityPassActionCreators.setSelectedOfferAction(data.items[0]));
        }
      } catch (_err: any) {
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  getPriceMunichCityPassPrebookAction:
    (params: any, transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState()) || "";
      const paths = resolveMunichCityPassApiPaths(transportContext);
      const passengerList = getState().munichCityPass.passengerList as MunichCityPassPassenger[];
      const selectedOffer = getState().munichCityPass.selectedOffer as MunichCityPassOffer | null;
      const requestOfferCode = params?.offer_code ?? selectedOffer?.offer_code ?? null;

      const updatePassengers = passengerList.map(passenger => {
        const birthDate = passenger?.birthDate;
        return {
          first_name: passenger?.firstName,
          last_name: passenger?.lastName,
          birthdate: birthDate ? getFormattedDate(birthDate) : null
        };
      });

      const rawStartDate = typeof params?.start_date === "string" ? params.start_date : "";
      const [datePart, timePart] = rawStartDate.split(" ");

      const basePayload = buildDestinationTransportPayload({
        date: datePart || "",
        time: timePart || "",
        departureAddress: params?.departureAddress ?? params?.origin ?? null,
        departureAddressId: params?.departureAddressId ?? null,
        destinationAddress: params?.destinationAddress ?? params?.destination ?? null,
        destinationAddressId: params?.destinationAddressId ?? null,
        milesAndMore: params?.milesAndMore ?? readAirlineMilesAndMoreNumber(),
        offerCode: requestOfferCode
      });

      const apiParams: Record<string, any> = {
        ...basePayload,
        details: {
          passengers: updatePassengers
        }
      };

      const isStaleEstimationResponse = () => {
        const currentOfferCode = getState().munichCityPass.selectedOffer?.offer_code ?? null;
        return Boolean(requestOfferCode) && currentOfferCode !== requestOfferCode;
      };

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
      dispatch({
        type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR,
        prebookingError: null
      });

      if (paths.kind === "invalid_airline") {
        dispatch({
          type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: null
        });
        dispatch({
          type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR,
          prebookingError: { code: "MISSING_IATA", message: "Missing airport code" }
        });
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
        return;
      }

      try {
        let data: any;

        if (USE_MOCK_MUNICH_CITY_PASS) {
          await new Promise(resolve => setTimeout(resolve, 300));
          if (USE_MOCK_MUNICH_CITY_PASS_ESTIMATION_ERROR) {
            // Mimic an axios error so the real catch path handles it
            throw Object.assign(new Error("Validation failed"), {
              response: { status: 400, data: MOCK_MUNICH_CITY_PASS_ESTIMATION_ERROR_RESPONSE }
            });
          }
          data = MOCK_MUNICH_CITY_PASS_ESTIMATION_RESPONSE;
        } else {
          data = await postDestinationTransportEstimation({
            apiUrl,
            transportContext,
            payload: apiParams,
            v2: { path: "v2/transport/munich-city-pass" },
            airline: {
              path: iataCode => `v1/airports/${encodeURIComponent(iataCode)}/transports/munich-city-pass`
            }
          });
        }

        // Ignore late responses after the guest changed the selected offer mid-request.
        if (isStaleEstimationResponse()) {
          return;
        }

        dispatch({
          type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: data,
          travelDate: datePart
        });
        return data;
      } catch (err: any) {
        if (isStaleEstimationResponse()) {
          return;
        }

        dispatch({
          type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_PREBOOKING_RESPONSE_INFO,
          prebookingInfoResponse: null
        });
        dispatch({
          type: munichCityPassActionTypes.UPDATE_MUNICH_CITY_PASS_PREBOOK_API_ERROR,
          prebookingError: err?.response?.data
        });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },
  buyMunichCityPassTicketAction:
    (params: any, navigate: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const state = getState().munichCityPass;
      const dataForBooking = state.dataForBooking as MunichCityPassBookingForm;
      const passengerList = state.passengerList as MunichCityPassPassenger[];
      const selectedOffer = state.selectedOffer as MunichCityPassOffer | null;
      const prebookingInfoResponse = state.prebookingInfoResponse;
      const paths = resolveMunichCityPassApiPaths(transportContext);

      const errorField = {
        nameForm: checkForEmptyField(dataForBooking.nameForm),
        emailForm: isEmailInvalid(dataForBooking.emailForm) || checkForEmptyField(dataForBooking.emailForm),
        commentForm: false
      };

      dispatch(munichCityPassActionCreators.toggleBookingErrorAction(false));

      if (Object.values(errorField).some(Boolean)) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField });
        return;
      }

      if (!selectedOffer || !passengerList.length || paths.kind === "invalid_airline") {
        dispatch(munichCityPassActionCreators.toggleBookingErrorAction(true));
        return;
      }

      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField });
      localStorage.removeItem("bookingResponseDataStorage");

      const apiUrl = selectApiBaseUrl(getState());
      const language = localStorage.getItem("i18nextLng") || "en";

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      const fullName = extractFullNameParts(dataForBooking.nameForm);

      const apiParams = {
        summary: {
          start_date: params?.selectedDate,
          adult: passengerList.length,
          timeslot: "00:00",
          offer_code: params?.offer_code ?? selectedOffer.offer_code
        },
        personal_information: {
          first_name: fullName.firstName,
          last_name: fullName.lastName,
          email: dataForBooking.emailForm,
          phone: "",
          comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
        },
        ...buildAirlineMilesAndMoreBookingFields(transportContext),
        session_id: prebookingInfoResponse?.details?.session_id || prebookingInfoResponse?.session_id
      };

      try {
        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

        // We can get API status 200, with booking_status=error --> in that case we should show the error page instead of Payment Modal / Confirmation Page
        if (response.data?.booking_status === "error") {
          dispatch(munichCityPassActionCreators.toggleBookingErrorAction(true));
          return;
        }

        dispatch(munichCityPassActionCreators.toggleBookingErrorAction(false));
        dispatch({
          type: munichCityPassActionTypes.SET_MUNICH_CITY_PASS_BOOKING_RESPONSE_DATA,
          bookingResponseData: response.data
        });

        // If price is 0 we will get payment_status=paid from API --> and we should redirect user directly to the /confirmation page (without payment modal)
        if (response.data?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(`/${language}/transport/munich-city-pass/confirmation`, { replace: true });
        } else {
          dispatch(munichCityPassActionCreators.togglePaymentModalAction(true));
        }
      } catch (error: any) {
        handleErrorAlert(error);
        dispatch(munichCityPassActionCreators.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    }
};
